import { trackEvent, isTeamDevice } from "@/lib/analytics";
import { getSupabase } from "@/lib/supabase-lazy";
import { normTool, channelOf, toolVariantOf } from "@/lib/toolNames";
import { rememberChannel } from "@/lib/captureChannel";
import { toDisplayPlan } from "@/lib/planAnalytics";

/**
 * Analítica ligera de landings (visitas, cuestionario, CTAs, registros).
 *
 * Se escribe en `landing_events` desde el cliente (anon permitido) y se lee
 * únicamente desde el Back Office (/admin/landings).
 */

export type LandingEventType =
  | "page_view"
  | "quiz_start"
  | "quiz_step"
  | "quiz_answer"
  | "quiz_complete"
  | "cta_click"
  | "signup_completed"
  | "click"
  | "share_click";

export interface LandingEventPayload {
  landingSlug: string;
  eventType: LandingEventType;
  lang?: string;
  step?: string;
  questionKey?: string;
  answerIndex?: number;
  answerLabel?: string;
  plan?: "free" | "pro" | "lite" | "panel" | string;
  metadata?: Record<string, unknown>;
}

const SESSION_KEY = "rl_landing_session";

const getSessionId = (): string => {
  if (typeof window === "undefined") return "ssr";
  try {
    const existing = window.localStorage.getItem(SESSION_KEY);
    if (existing) return existing;
    const id =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `s_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    window.localStorage.setItem(SESSION_KEY, id);
    return id;
  } catch {
    return `s_${Date.now()}`;
  }
};

const QUIZ_LANDINGS = new Set(["lp-instagram", "test-supervivencia"]);

/** Evento GA4 de las landings con quiz (nombres y parámetros ya existentes en GTM). */
export function gaForLandingEvent(slug: string, eventType: string, plan: string | null): { event: string; params: Record<string, string | number | undefined> } | null {
  if (!QUIZ_LANDINGS.has(slug)) return eventType === "quiz_complete" ? { event: "tool_use", params: { tool_name: "quiz" } } : null;
  const base = { tool_name: "quiz", tool_variant: slug };
  if (eventType === "quiz_start") return { event: "tool_step_started", params: { ...base, step_index: 1, step_name: "inicio" } };
  if (eventType === "quiz_complete") return { event: "tool_use", params: base };
  if (eventType === "cta_click") return { event: "signup_cta_click", params: { ...base, plan: plan ?? undefined } };
  return null;
}

/** Evita duplicar el mismo evento en la misma carga de página. */
const firedOnce = new Set<string>();

export const trackLandingEvent = async (
  payload: LandingEventPayload,
  options?: { once?: boolean; skipGa?: boolean },
): Promise<void> => {
  if (typeof window === "undefined") return;

  const plan = payload.plan ? toDisplayPlan(payload.plan) : null;
  const dedupeKey = `${payload.landingSlug}:${payload.eventType}:${payload.step ?? ""}:${payload.questionKey ?? ""}:${plan ?? ""}`;
  if (options?.once) {
    if (firedOnce.has(dedupeKey)) return;
    firedOnce.add(dedupeKey);
  }

  if (payload.eventType === "page_view" || payload.eventType === "quiz_start") rememberChannel(channelOf(payload.landingSlug));

  if (!options?.skipGa) {
    const ga = gaForLandingEvent(payload.landingSlug, payload.eventType, plan);
    if (ga) trackEvent(ga.event, ga.params);
    // Clic de pago en la landing de una herramienta: GA4 lo recibe como tool_cta_click (mismo clic).
    const tool = normTool(payload.landingSlug);
    if (payload.eventType === "cta_click" && tool && plan && plan !== "sin_plan") {
      const variant = toolVariantOf(payload.landingSlug, payload.metadata);
      trackEvent("tool_cta_click", { tool_name: tool, cta_type: "pago", ...(variant ? { tool_variant: variant } : {}) });
    }
  }

  try {
    // Tráfico interno fuera: solo dominios oficiales y nunca admins ni dispositivos del equipo.
    if (!/(^|\.)retaillemon\.com$/i.test(window.location.hostname)) return;
    const { isInternalVisitor } = await import("@/lib/publicActivity");
    if (await isInternalVisitor()) return;
    const params = new URLSearchParams(window.location.search);
    const supabase = await getSupabase();
    const { data } = await supabase.auth.getSession();
    // Mismo visitante que public_activity (rl_vid), para unir la vista con el clic de pago.
    const { getPublicSessionKey } = await import("@/lib/publicActivity");
    let rlVid: string | null = null; try { rlVid = getPublicSessionKey() || null; } catch { /* nada */ }

    await supabase.from("landing_events").insert({
      landing_slug: payload.landingSlug,
      event_type: payload.eventType,
      session_id: getSessionId(),
      user_id: data.session?.user?.id ?? null,
      lang: payload.lang ?? null,
      step: payload.step ?? null,
      question_key: payload.questionKey ?? null,
      answer_index: payload.answerIndex ?? null,
      answer_label: payload.answerLabel ?? null,
      plan,
      path: window.location.pathname,
      referrer: document.referrer || null,
      utm_source: params.get("utm_source"),
      utm_medium: params.get("utm_medium"),
      utm_campaign: params.get("utm_campaign"),
      metadata: ({ ...(payload.metadata ?? {}), ...(rlVid ? { rl_vid: rlVid } : {}), ...(isTeamDevice() ? { internal: true } : {}) }) as never,
    });
  } catch {
    // La analítica nunca debe romper la landing.
  }
};
